import { timeYear, timeDay } from 'd3-time';
import { getEasternTimeFromDate, getLocalTimeFromEasternDate } from './daylight-savings-time';
import { scaleLinear } from 'd3-scale';
import localtime from '@os/localtime';
import { FIRST_HOUR, SECONDS_PER_FINANCE_DAY, SECONDS_PER_HOUR, SECONDS_PER_MINUTE, SECONDS_PER_FINANCE_WEEK,
	SECONDS_PER_FINANCE_MONTH, SECONDS_PER_FINANCE_YEAR, SATURDAY, SUNDAY, WEEKDAYS_PER_WEEK, BASE_YEAR, FIRST_SECOND_OF_FINANCE_DAY,
	DAYS_PER_WEEK, SHIFT_MONDAY_TO_DAY_0, DECEMBER, MONDAY, SHIFT_WEEKEND_TO_WEEKDAY, SHIFTED_SATURDAY, SHIFTED_FRIDAY,
	SHIFTED_SUNDAY, WEEKEND_DAYS_PER_WEEK } from './finance-constants';
import {	decadelyInterval, yearlyInterval, quarterlyInterval, monthlyInterval, weeklyInterval, dailyInterval, bihourlyInterval,
	hourlyInterval, quarterHourlyInterval, fiveMinutelyInterval, minutelyInterval, halfMinutelyInterval, secondlyInterval } from './finance-time-intervals';

/**
 * Meant to be a drop-in replacement for d3-scale/scaleTime, but uses the financial calendar instead of a regular
 * calendar. i.e. Only M-F, 9:00am-5:00pm are used in the scale.  The difference is that it expects
 * the domain to be from the financeScale function in this file instead of straight date/epoch values
 *
 * @see https://github.com/d3/d3-scale#time-scales for provided functions
 */
export function scaleFinanceTime() {
	let scale = scaleLinear();

	scale.ticks = function(count) {
		let d = this.domain();
		return getTickGenerator(d)(d[0], d[1], count);
	};

	scale.tickFormat = function(count, specifier) {
		return specifier ? (tick) => localtime.format(specifier)(getEasternTimeFromDate(financeScaleInvert(tick))) : formatTick;
	};

	return scale;
}

/**
 * Return an appropriate interval generator for the span of the domain
 * e.g. if the domain spans 2 years or more, it will return a yearlyInterval function,
 * if the domain spans 7 months-2 years, it will return a quarterlyInterval function, etc.
 *
 * @param {Array} domain The domain array from a scale with 2 values, domain[0] is the
 *		beginning of the domain and domain[1] is the end of the domain as ticks from fiannceScale
 * @returns {Function} an interval function like yearlyInterval that given a domain will return appropriate ticks for an axis of that domain
 */
function getTickGenerator(domain) {
	let tickSpan = domain[1]-domain[0]+1;

	/* eslint-disable no-magic-numbers */
	/* eslint-disable indent */
	return tickSpan < 6 ? secondlyInterval
		: tickSpan < 3*SECONDS_PER_MINUTE ? halfMinutelyInterval
		: tickSpan < 6*SECONDS_PER_MINUTE ? minutelyInterval
		: tickSpan < 30*SECONDS_PER_MINUTE ? fiveMinutelyInterval
		: tickSpan < 90*SECONDS_PER_MINUTE ? quarterHourlyInterval
		: tickSpan < 6*SECONDS_PER_HOUR ? hourlyInterval
		: tickSpan < 12*SECONDS_PER_HOUR ? bihourlyInterval
		: tickSpan < 6*SECONDS_PER_FINANCE_DAY ? dailyInterval
		: tickSpan < 6*SECONDS_PER_FINANCE_WEEK ? weeklyInterval
		: tickSpan < 7*SECONDS_PER_FINANCE_MONTH ? monthlyInterval
		: tickSpan < 2*SECONDS_PER_FINANCE_YEAR ? quarterlyInterval
		: tickSpan < 11 * SECONDS_PER_FINANCE_YEAR ? yearlyInterval
		: decadelyInterval;

	/* eslint-enable indent */
	/* eslint-enable no-magic-numbers */
}

// Format definitions used in formatTick
const formatSecond = localtime.format(':%S'), // :05
	formatMinute = localtime.format('%I:%M'), // 04:05 (12-hour clock)
	formatHour = localtime.format('%I %p'), // 04 PM
	formatDay = localtime.format('%a %d'), // Fri 23
	formatWeek = localtime.format('%b %d'), // Jan 23
	formatMonth = localtime.format('%B'), // January
	formatYear = localtime.format('%Y'); // 2016

/**
 * Given a tick value, format the tick in a "smart" way such that as you zoom in and out
 * on a scale you see years when zoomed out far and seconds when zoomed way in
 * It does this by formatting the tick based on whether it falls on a given fidelity
 * boundary.
 * The first tick of a year will be formatted as yearly - 2016
 * The first tick of the month that isn't the first tick of the year will be formatted as monthly - February
 * The first tick of the week that isn't the first tick of the month or year will be formatted as weekly - Jan 23
 * ...etc. all the way down through daily, hourly, minutely, and secondly
 *
 * @param {Integer} tick from financeScale
 * @returns {String} The tick formatted as a date string according to the rules above
 */
function formatTick(tick) {
	let tickDate = getEasternTimeFromDate(financeScaleInvert(tick));
	let d = new Date(tickDate);

	//get the first
	let firstTickOfYear  = financeScale(getEasternTimeFromDate(new Date(d.getFullYear(), 0))) + 1;
	if (tick === firstTickOfYear) return formatYear(tickDate);

	/* Steadily decrease the fidelity of the tick date.  If a particular decrease
		in fidelity yields a lower date, that is your fidelity for formatting. */
	d.setMilliseconds(0);
	d.setSeconds(0);
	if (d < tickDate) return formatSecond(tickDate);

	d.setMinutes(0);
	if (d < tickDate) return formatMinute(tickDate);

	d.setHours(FIRST_HOUR);
	if (d < tickDate) return formatHour(tickDate);

	/* Weekly format check.  If day is a Monday, it is first of week,
	 	but could also be first of month.  If first of month, format as monthly
		otherwise format as weekly */
	if ( d <= tickDate && d.getDay() === MONDAY) {
		d.setDate(1);
		let dayOfWeek = (d.getDay() + SHIFT_MONDAY_TO_DAY_0) % DAYS_PER_WEEK;
		//if on a Weekend, set date to the first following Monday
		if (dayOfWeek > SHIFTED_FRIDAY) d.setDate(Math.abs(dayOfWeek - DAYS_PER_WEEK - 1));
		return (d < tickDate) ? formatWeek(tickDate) : formatMonth(tickDate);
	}

	d.setDate(1);
	if (d < tickDate) return formatDay(tickDate);

	d.setMonth(0);
	if (d < tickDate) return formatMonth(tickDate);

	return formatYear(tickDate);
}

/**
 * A linear scale where 1 tick is equal to 1 second.  It only includes Monday-Friday,
 * 9:00am - 5:00 pm Eastern time (inclusive) as "tickable" times.  Any days/times outside of that range
 * map to the previous valid tick value (i.e. a floor function).
 * @param {Date} date A date object or an epoch time number representing a date.
 * @returns {Integer} scaleValue An integer value representing the date's place in the linear scale
 */
export function financeScale(date) {
	if (!date) return undefined;
	let dateInEasternTZ = getEasternTimeFromDate(date);

	let dayInYear = weekdayOfYear(dateInEasternTZ),
		year = dateInEasternTZ.getFullYear(),
		weekdays = dayInYear + weekdaysBeforeYear(year),
		secondsInDay = financeSecondsOfDay(dateInEasternTZ),
		isWeekDay = dateInEasternTZ.getDay() > SUNDAY && dateInEasternTZ.getDay() < SATURDAY;
	if ((isWeekDay && secondsInDay === null) || (!isWeekDay && dayInYear === 0)) {
		//don't count the current weekday if we haven't reached the first valid second of the day or year
		weekdays--;
		secondsInDay = SECONDS_PER_FINANCE_DAY - 1;
	}
	else if (!isWeekDay) {
		//treat weekend day like end of Friday
		secondsInDay = SECONDS_PER_FINANCE_DAY - 1;
	}

	return weekdays*SECONDS_PER_FINANCE_DAY + secondsInDay;
}

// The inverse of financeScale.  Given a scale value, returns a date;
export function financeScaleInvert(seconds) {
	if (typeof seconds === 'undefined') return undefined;

	let year = BASE_YEAR,
		yearWeekdays;

	let weekdays = (seconds / SECONDS_PER_FINANCE_DAY) | 0;
	let secondsInDay = seconds - weekdays * SECONDS_PER_FINANCE_DAY;

	// Compute the year and the remaining weekdays in the last year
	while ((yearWeekdays = weekdaysInYear(year)) <= weekdays) {
		++year;
		weekdays -= yearWeekdays;
	}

	// Compute the date from the remaining weekdays.
	let days = weekdays % WEEKDAYS_PER_WEEK,
		day0 = ((new Date(year, 0, 1)).getDay() + SHIFT_MONDAY_TO_DAY_0) % DAYS_PER_WEEK;
	if (day0 + days > SHIFTED_FRIDAY) days += SHIFT_WEEKEND_TO_WEEKDAY;
	//fix for years that start on Sunday
	if (day0 === SHIFTED_SUNDAY) days -= 1;
	return getLocalTimeFromEasternDate(new Date(year, 0, (weekdays / WEEKDAYS_PER_WEEK | 0) * DAYS_PER_WEEK + days + 1, 0, 0, FIRST_SECOND_OF_FINANCE_DAY + secondsInDay));
}

/**
 * Return 0-based seconds in the finance day.  0 starts at the first second of the finance day (e.g. 9:00:00 am)
 * and counts up 1 each seconds until the the end of the finance day e.g. 5:00:00 pm Eastern (inclusive).
 *
 * @param {Date} date The date to analyze the time portion of to get the finance seconds in its day
 * @returns {Integer} The number of 0 based seconds.  If the time is before the start of the finance day on a given day, return null.
 * If time is after end of finance day, return the same value as end of finance day
 */
export function financeSecondsOfDay(date) {
	let seconds = date.getHours()*SECONDS_PER_HOUR + date.getMinutes()*SECONDS_PER_MINUTE + date.getSeconds();
	if (seconds < FIRST_SECOND_OF_FINANCE_DAY) return null;
	return Math.min(SECONDS_PER_FINANCE_DAY-1, seconds - FIRST_SECOND_OF_FINANCE_DAY);
}

/**
 * Returns the number of weekdays in the specified year.  Similar to Array.length,
 * it is the true length, even though weekdayOfYear is 0 based
 *
 * @param {Integer} year The full year (e.g. 2016) to get get the number of weekdays for
 * @returns {Integer} Number of weekdays in year
 */
export function weekdaysInYear(year) {
	let LAST_DAY = 31;
	return weekdayOfYear(new Date(year, DECEMBER, LAST_DAY)) + 1;
}

let weekdaysBeforeYearCache = {};

/**
 * Given a year return how many weekdays were in all of the years
 * from 1970 up to that year exclusive
 * @example weekdaysBeforeYear(2000) would return the sum of weekdays in the years 1970-1999
 *
 * @param {Integer} year
 * @returns {Integer} weekdays Number of weekdays in all of the years between 1970 and this year
 */
function weekdaysBeforeYear(year) {
	if (Object.prototype.hasOwnProperty.call(weekdaysBeforeYearCache, year)) return weekdaysBeforeYearCache[year];
	return year <= BASE_YEAR ? 0 : weekdaysBeforeYearCache[year] = weekdaysInYear(year-1) + weekdaysBeforeYear(year-1);
}

/**
 * Get the 0-based weekday number for the given date relative to the start of the year. i.e. First weekday of year is 0
 * seconds weekday of year is 1, etc.  Saturday and Sunday report the value for the prior Friday.
 * @param {Date} date
 * @returns {Integer} 0-based weekday number for the date
 */
export function weekdayOfYear(date) {
	let year = date.getFullYear();
	let daysEllapsedInYear = timeDay.count(timeYear(date), date),
		weeks = daysEllapsedInYear / DAYS_PER_WEEK | 0, // bitwise or is same as floor to int value
		//Day of week (Monday=0, Sunday=6) for 1st day of year
		day0 = (new Date(year, 0, 1).getDay() + SHIFT_MONDAY_TO_DAY_0) % DAYS_PER_WEEK,
		//Day of week (Monday=0, Sunday=6) for date argument
		dayDate = (new Date(year, 0, daysEllapsedInYear+1).getDay() + SHIFT_MONDAY_TO_DAY_0) % DAYS_PER_WEEK,
		cumulativeWeekendDays = weeks * WEEKEND_DAYS_PER_WEEK;

	if (day0 === SHIFTED_SATURDAY) {
		//If Year started on a Saturday, add two weekend days, except if date is Saturday, where we only add 1
		cumulativeWeekendDays += dayDate === SHIFTED_SATURDAY ? 1 : WEEKEND_DAYS_PER_WEEK;
	}
	else if (day0 === SHIFTED_SUNDAY) {
		//If Year started on a Sunday, add 1 weekend day, except Saturday, where add 2
		cumulativeWeekendDays += dayDate === SHIFTED_SATURDAY ? WEEKEND_DAYS_PER_WEEK : 1;
	}
	else {
		//year started on a weekday
		//add 1 for Saturday and 2 for Sunday or if we don't have a full week if year started on a weekday
		cumulativeWeekendDays += dayDate < day0 || dayDate === SHIFTED_SUNDAY ? WEEKEND_DAYS_PER_WEEK :
		 dayDate === SHIFTED_SATURDAY ? 1 : 0;
	}

	//Remove the number of weekend days so far in this year from the ellapsed day total
	//Adjust for years that start on Saturday or Sunday
	return Math.max(0, daysEllapsedInYear - cumulativeWeekendDays);
}
